import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Package, ToggleLeft, ToggleRight, Tag, Clock, User } from 'lucide-react';
import theme from '../theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreatorMyListings = ({ user }) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/creator/my-utility-listings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(res.data.listings || []);
    } catch (err) {
      console.error('Error fetching listings:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (listing) => {
    const newStatus = listing.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setToggling(listing.id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/provider/utility-listings/${listing.id}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setListings(prev => prev.map(l => l.id === listing.id ? { ...l, status: newStatus } : l));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setToggling(null);
    }
  };

  const getPricingLabel = (model, amount, currency) => {
    if (model === 'FREE') return 'Free';
    if (model === 'PAID') return `${currency} ${amount || '—'}`;
    return 'Rev Share';
  };

  if (loading) {
    return <div style={{ padding: '40px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading your listings...</div>;
  }

  return (
    <div style={{ padding: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
        <div>
          <h1 data-testid="my-listings-title" style={{
            fontFamily: theme.fonts.heading,
            fontSize: '28px',
            fontWeight: 700,
            color: theme.colors.text.primary,
            marginBottom: '6px'
          }}>
            My Utility Listings
          </h1>
          <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>
            Manage your published utility offerings ({listings.length} listing{listings.length !== 1 ? 's' : ''})
          </p>
        </div>
        <button
          data-testid="new-listing-btn"
          onClick={() => navigate('/creator/publish-utility')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: theme.colors.primary,
            color: '#FFFFFF',
            border: 'none',
            borderRadius: theme.radius.sm,
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          <Plus size={18} />
          New Listing
        </button>
      </div>

      {listings.length === 0 ? (
        <div style={{
          background: theme.colors.bg.card,
          borderRadius: theme.radius.card,
          padding: '60px',
          textAlign: 'center',
          border: `1px solid ${theme.colors.borders}`
        }}>
          <Package size={48} style={{ color: theme.colors.text.muted, marginBottom: '16px' }} />
          <h3 style={{ color: theme.colors.text.primary, fontSize: '18px', marginBottom: '8px' }}>
            No Listings Yet
          </h3>
          <p style={{ color: theme.colors.text.secondary, fontSize: '14px', marginBottom: '20px' }}>
            Publish your first utility to appear on the marketplace
          </p>
          <button
            onClick={() => navigate('/creator/publish-utility')}
            style={{
              padding: '10px 24px',
              background: theme.colors.primary,
              color: '#FFFFFF',
              border: 'none',
              borderRadius: theme.radius.sm,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Publish First Utility
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {listings.map((listing) => (
            <div
              key={listing.id}
              data-testid={`my-listing-${listing.id}`}
              onClick={() => navigate(`/creator/listings/${listing.id}`)}
              style={{
                background: theme.colors.bg.card,
                borderRadius: theme.radius.card,
                border: `1px solid ${theme.colors.borders}`,
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                boxShadow: theme.colors.shadows.sm,
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = theme.colors.shadows.md}
              onMouseLeave={e => e.currentTarget.style.boxShadow = theme.colors.shadows.sm}
            >
              {/* Icon */}
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: listing.status === 'ACTIVE' ? `${theme.colors.status.success}15` : `${theme.colors.text.muted}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Package size={20} style={{ color: listing.status === 'ACTIVE' ? theme.colors.status.success : theme.colors.text.muted }} />
              </div>

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <h3 style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: theme.colors.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {listing.title}
                  </h3>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 600,
                    background: listing.status === 'ACTIVE' ? `${theme.colors.status.success}15` : `${theme.colors.status.warning}15`,
                    color: listing.status === 'ACTIVE' ? theme.colors.status.success : theme.colors.status.warning
                  }}>
                    {listing.status}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: theme.colors.text.muted }}>
                  <span>{listing.fulfillment_type}</span>
                  <span>{getPricingLabel(listing.pricing_model, listing.price_amount, listing.currency)}</span>
                  {listing.category_tags?.length > 0 && <span>{listing.category_tags.join(', ')}</span>}
                </div>
              </div>

              {/* Toggle */}
              <button
                data-testid={`toggle-listing-${listing.id}`}
                onClick={(e) => { e.stopPropagation(); toggleStatus(listing); }}
                disabled={toggling === listing.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  background: 'transparent',
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: theme.radius.sm,
                  fontSize: '13px',
                  color: listing.status === 'ACTIVE' ? theme.colors.status.warning : theme.colors.status.success,
                  cursor: 'pointer',
                  opacity: toggling === listing.id ? 0.5 : 1
                }}
              >
                {listing.status === 'ACTIVE' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                {listing.status === 'ACTIVE' ? 'Pause' : 'Activate'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CreatorMyListings;
